import { ABSENCE_STATUSES, calendarBars, LEAVE_KINDS, type CalendarDto, type CalendarEntryDto, type CalendarItemDto, type ConflictDto, type MeDto, readableTextColor, shiftDate, taskDays, todayInWarsaw } from '@bqcrm/shared';
import { ActionIcon, Alert, Badge, Box, Button, Card, Group, Loader, Modal, MultiSelect, ScrollArea, SegmentedControl, Select, Stack, Text, Tooltip } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconChevronLeft, IconChevronRight, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { ask } from '../../confirm';
import { EmptyState } from '../../components/ui';
import { dayRange, monthPl, timeHm } from '../../format';
import { notifyError, notifyOk, notifyWarn } from '../../notify';
import { ConflictList, dayLabel, longDay, usePeople, useTeams } from './common';
import { ApprovalTrail, DecisionButtons, PendingAbsences } from './AbsenceApproval';
import { AttendanceList } from './AttendanceList';
import { EntryDialog } from './EntryDialog';
import { AbsenceEditDialog, DeputyInfo } from './AbsenceEditDialog';
import { TaskPanel } from './TaskPanel';
import { FleetCalendarRows } from '../fleet/FleetCalendarRows';

type Mode = 'tydzien' | 'miesiac' | 'dzien';
const KIND_BG: Record<string, string> = { nieobecnosc: 'var(--app-absence)', wydarzenie: 'var(--app-chip-event)', wpis: 'var(--app-chip-entry)' };
const mondayOf = (d: string) => shiftDate(d, -((new Date(`${d}T12:00:00Z`).getUTCDay() + 6) % 7));
const HOURS = Array.from({ length: 13 }, (_, i) => 6 + i); // 6:00–18:00
const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
/** wysokość godziny w widoku dnia */
const HOUR_H = 56;
const WEEKDAYS = ['pn', 'wt', 'śr', 'cz', 'pt', 'sb', 'nd'];
/** kolumny widoku tygodnia: pracownik + 7 dni (nagłówek i wiersze osób — ten sam układ) */
const WEEK_COLS = '200px repeat(7, minmax(130px, 1fr))';

/** Nagłówek dnia: dzień tygodnia nad datą, dziś wyróżnione. */
function DayHead({ d, today }: { d: string; today: string }) {
  const l = dayLabel(d);
  const isToday = d === today;
  return (
    <Box
      py={6}
      ta="center"
      style={{
        borderLeft: '1px solid var(--app-line)',
        background: isToday ? 'var(--app-today-strong)' : l.weekend ? 'var(--app-subtle)' : undefined,
        boxShadow: isToday ? 'inset 0 -3px 0 var(--mantine-color-blue-6)' : undefined,
      }}
    >
      <Text size="xs" tt="uppercase" fw={isToday ? 700 : 500} c={isToday ? 'blue.7' : l.weekend ? 'red' : 'dimmed'} lh={1.3}>
        {isToday ? `dziś · ${l.wd}` : l.wd}
      </Text>
      <Text size="md" fw={700} c={isToday ? 'var(--app-text-blue)' : undefined} lh={1.3}>
        {l.dm}
      </Text>
    </Box>
  );
}

/** Dwa rzędy wpisu: tytuł zadania (bez numeru) i numer z godzinami; nieobecność / wpis — tytuł i stan albo godziny. */
function twoLines(i: CalendarItemDto): { main: string; sub: string } {
  const hours = i.timeFrom ? `${i.timeFrom}–${i.timeTo ?? ''}` : 'cały dzień';
  if (i.kind === 'zadanie') {
    if (i.restricted) return { main: i.title, sub: hours };
    const cut = i.title.indexOf(' · ');
    return cut > 0 ? { main: i.title.slice(cut + 3), sub: `${i.title.slice(0, cut)} · ${hours}` } : { main: i.title, sub: hours };
  }
  const status = i.kind === 'nieobecnosc' && i.status ? ABSENCE_STATUSES[i.status as keyof typeof ABSENCE_STATUSES] : null;
  return { main: i.title, sub: [status, i.timeFrom ? hours : null, i.deputyName ? `zastępuje: ${i.deputyName}` : null].filter(Boolean).join(' · ') || hours };
}

/** Wpis w kalendarzu: kafelek dnia albo słupek przez kilka dni (`edges` — czy to prawdziwy początek / koniec wpisu). */
function Item({ i, onOpen, fill, edges }: { i: CalendarItemDto; onOpen: (i: CalendarItemDto) => void; fill?: boolean; edges?: { start: boolean; end: boolean } }) {
  const bg = i.kind === 'zadanie' ? (i.color ?? '#adb5bd') : KIND_BG[i.kind];
  return (
    <Tooltip label={`${i.title}${i.timeFrom ? `, ${i.timeFrom}–${i.timeTo}` : ''}${i.status && i.kind === 'nieobecnosc' ? ` (${ABSENCE_STATUSES[i.status as keyof typeof ABSENCE_STATUSES]})` : ''}${i.deputyName ? ` · zastępuje: ${i.deputyName}` : ''}`} multiline w={260}>
      <Box
        onClick={() => onOpen(i)}
        style={{
          background: bg,
          color: i.kind === 'zadanie' ? (i.restricted ? 'var(--mantine-color-dark-7)' : readableTextColor(bg ?? '')) : 'var(--app-chip-text)',
          borderRadius: edges ? `${edges.start ? 5 : 0}px ${edges.end ? 5 : 0}px ${edges.end ? 5 : 0}px ${edges.start ? 5 : 0}px` : 5,
          padding: edges ? '4px 8px' : '3px 6px',
          marginBottom: edges ? 0 : 3,
          marginLeft: edges && edges.start ? 3 : 0,
          marginRight: edges && edges.end ? 3 : 0,
          minWidth: 0,
          height: fill ? '100%' : undefined,
          overflow: 'hidden',
          cursor: i.restricted ? 'default' : 'pointer',
          opacity: i.kind === 'nieobecnosc' && i.status === 'wniosek' ? 0.7 : 1,
          // obrys zamiast ramki — ta sama wysokość co pozostałe wpisy
          outline: i.kind === 'nieobecnosc' && i.status === 'wniosek' ? '1px dashed var(--mantine-color-red-6)' : undefined,
          outlineOffset: -1,
        }}
      >
        {edges ? (
          // tydzień / miesiąc: dwa rzędy tej samej wysokości dla wpisów jednodniowych i wielodniowych —
          // czego dotyczy (tytuł) oraz numer zadania i godziny (nieobecność: stan)
          <>
            <Text size="sm" fw={i.kind === 'zadanie' ? 600 : 500} lh="20px" truncate>
              {twoLines(i).main}
            </Text>
            <Text size="xs" lh="16px" truncate style={{ opacity: 0.85 }}>
              {twoLines(i).sub}
            </Text>
          </>
        ) : (
          <>
            {/* widok dnia: godzina w osobnej linii, tytuł do dwóch linii */}
            {i.timeFrom && (
              <Text size="xs" fw={700} lh={1.3}>
                {i.timeFrom}–{i.timeTo}
              </Text>
            )}
            <Text size="sm" fw={i.kind === 'zadanie' ? 600 : 500} lh={1.3} lineClamp={2} style={{ wordBreak: 'break-word' }}>
              {i.title}
            </Text>
          </>
        )}
      </Box>
    </Tooltip>
  );
}

function EntryPanel({ me, entry, people, onClose }: { me: MeDto; entry: CalendarEntryDto; people: Map<string, string>; onClose: () => void }) {
  const [edit, setEdit] = useState(false);
  const qc = useQueryClient();
  const phone = useMediaQuery('(max-width: 48em)');
  const cancel = useMutation({
    mutationFn: () => api(`/calendar/entries/${entry.id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['calendar'] });
      void qc.invalidateQueries({ queryKey: ['planning-board'] });
      notifyOk('Wycofano wpis');
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={entry.title} size={entry.attendanceRequired ? 'lg' : undefined} fullScreen={phone && entry.attendanceRequired}>
      <Stack gap="xs">
        <Text size="sm">
          {dayRange(entry.dateFrom, entry.dateTo)}
          {entry.timeFrom ? `, ${entry.timeFrom}–${entry.timeTo}` : ', cały dzień'}
        </Text>
        {entry.absenceStatus && (
          <Badge color={entry.absenceStatus === 'zatwierdzona' ? 'green' : entry.absenceStatus === 'wniosek' ? 'yellow' : 'red'} variant="light" w="fit-content">
            {ABSENCE_STATUSES[entry.absenceStatus]}
          </Badge>
        )}
        {entry.leaveKind && <Text size="sm">Rodzaj urlopu: {LEAVE_KINDS[entry.leaveKind].toLowerCase()}</Text>}
        {entry.absenceStatus === 'odrzucona' && entry.decisionNote && (
          <Text size="sm" c="red">
            Powód odmowy: {entry.decisionNote}
          </Text>
        )}
        {entry.approval && (entry.approval.steps.length > 0 || entry.approval.current) && <ApprovalTrail approval={entry.approval} />}
        {entry.can.decide && (
          <Group justify="flex-end">
            <DecisionButtons entry={entry} onDone={onClose} />
          </Group>
        )}
        {!entry.attendanceRequired && <Text size="sm">{entry.allUsers ? 'Wszyscy pracownicy' : entry.userIds.map((u) => people.get(u) ?? '—').join(', ')}</Text>}
        <DeputyInfo entry={entry} />
        {entry.note && <Text size="sm">{entry.note}</Text>}
        {entry.attendanceRequired && (
          <>
            <Badge variant="light" color="eko" w="fit-content">
              z potwierdzeniem obecności
            </Badge>
            <AttendanceList entryId={entry.id} />
          </>
        )}
        <Text size="xs" c="dimmed">
          Dodał(a): {entry.createdByName ?? '—'}
          {entry.decidedByName ? ` · decyzja: ${entry.decidedByName}` : ''}
        </Text>
        {(entry.can.cancel || entry.can.editDates || entry.can.setDeputy) && (
          <Group justify="flex-end">
            {(entry.can.editDates || entry.can.setDeputy) && (
              <Button variant="default" onClick={() => setEdit(true)}>
                {entry.can.editDates ? 'Zmień daty / zastępcę' : 'Osoba zastępująca'}
              </Button>
            )}
            {entry.can.cancel && (
              <Button
                color="red"
                variant="subtle"
                onClick={async () => (await ask(entry.deputy ? 'Wpis zniknie z kalendarza i planu, a zastępstwo się zakończy.' : 'Wpis zniknie z kalendarza i planu.', { title: 'Wycofać wpis?', confirm: 'Wycofaj', danger: true })) && cancel.mutate()}
                loading={cancel.isPending}
              >
                Wycofaj wpis
              </Button>
            )}
          </Group>
        )}
      </Stack>
      {edit && <AbsenceEditDialog me={me} entry={entry} onClose={() => (setEdit(false), onClose())} />}
    </Modal>
  );
}

/** Kalendarz: tydzień (pracownicy × dni), miesiąc jednego pracownika, dzień godzinowy dla wielu pracowników. */
export function CalendarPage({ me }: { me: MeDto }) {
  const today = todayInWarsaw();
  const [mode, setMode] = useState<Mode>('tydzien');
  const [anchor, setAnchor] = useState(today);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [newEntry, setNewEntry] = useState<{ kind: 'nieobecnosc' | 'wydarzenie'; day?: string; initial?: { userId?: string; from?: string; to?: string; caseNumber?: string } } | null>(null);
  const [task, setTask] = useState<string | null>(null);
  const [entry, setEntry] = useState<CalendarEntryDto | null>(null);
  const teams = useTeams();
  // link z powiadomienia: `?dzien=RRRR-MM-DD&wpis=<id>` — kalendarz na dniu wpisu, okno wpisu otwarte (docs/16)
  const [search, setSearch] = useSearchParams();
  const [linked, setLinked] = useState<string | null>(null);
  useEffect(() => {
    // „Ustaw zastępstwo” (wstrzymana sprawa, dawne linki do „Moich zadań”): nowa nieobecność z osobą zastępującą — `?nowa=nieobecnosc&kto=&od=&do=&sprawa=`
    if (search.get('nowa') === 'nieobecnosc') {
      const d = (k: string) => {
        const v = search.get(k);
        return v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined;
      };
      setNewEntry({ kind: 'nieobecnosc', initial: { userId: search.get('kto') ?? undefined, from: d('od'), to: d('do'), caseNumber: search.get('sprawa') ?? undefined } });
      setSearch({}, { replace: true });
      return;
    }
    const day = search.get('dzien');
    const id = search.get('wpis');
    if (!day && !id) return;
    if (day && /^\d{4}-\d{2}-\d{2}$/.test(day)) setAnchor(day);
    setLinked(id);
    setSearch({}, { replace: true });
  }, [search, setSearch]);

  const monthStart = `${anchor.slice(0, 7)}-01`;
  const monthEnd = shiftDate(`${shiftDate(monthStart, 32).slice(0, 7)}-01`, -1);
  const [from, to] = mode === 'tydzien' ? [anchor, shiftDate(anchor, 6)] : mode === 'dzien' ? [anchor, anchor] : [mondayOf(monthStart), shiftDate(mondayOf(monthEnd), 6)];
  const params = new URLSearchParams({ from, to });
  if (teamId) params.set('teamId', teamId);
  if (userIds.length) params.set('userIds', userIds.join(','));
  const q = useQuery({ queryKey: ['calendar', params.toString()], queryFn: () => api<CalendarDto>(`/calendar?${params}`) });
  useEffect(() => {
    if (!linked || !q.data || q.data.from !== from || q.data.to !== to) return;
    const found = q.data.entries.find((e) => e.id === linked);
    if (found) setEntry(found);
    else notifyWarn('Tego wpisu nie ma już w kalendarzu — został wycofany albo jest niedostępny.');
    setLinked(null);
  }, [linked, q.data, from, to]);
  const people = new Map((q.data?.people ?? []).map((p) => [p.id, p.fullName]));
  const days = taskDays(from, to, false);
  const itemsOfUser = (u: string) => (q.data?.items ?? []).filter((i) => i.userId === u);
  const itemsOf = (u: string, d: string) =>
    (q.data?.items ?? []).filter((i) => i.userId === u && i.day === d).sort((a, b) => (a.timeFrom ?? '').localeCompare(b.timeFrom ?? ''));
  const open = (i: CalendarItemDto) => {
    if (i.restricted) return;
    if (i.kind === 'zadanie') setTask(i.refId);
    else setEntry(q.data?.entries.find((e) => e.id === i.refId) ?? null);
  };
  const step = (dir: number) => setAnchor(mode === 'dzien' ? shiftDate(anchor, dir) : mode === 'tydzien' ? shiftDate(anchor, 7 * dir) : `${shiftDate(monthStart, dir > 0 ? 32 : -1).slice(0, 7)}-01`);
  const monthPerson = userIds[0] ?? me.id;

  return (
    <>
      <PageHeader
        title="Kalendarz"
        description="Zadania, nieobecności, wydarzenia firmowe i wpisy organizacyjne."
        actions={
          <>
            <Button variant="light" leftSection={<IconPlus size={16} />} onClick={() => setNewEntry({ kind: 'nieobecnosc' })}>
              Dodaj nieobecność
            </Button>
            {can(me, 'kalendarz.wpisy') && (
              <Button leftSection={<IconPlus size={16} />} onClick={() => setNewEntry({ kind: 'wydarzenie' })}>
                Dodaj wydarzenie
              </Button>
            )}
          </>
        }
      />
      {/* wnioski czekające na moją decyzję — osoba z drabinki akceptacji nie musi mieć uprawnienia (docs/17) */}
      <PendingAbsences />
      <Group justify="space-between" mb="sm" wrap="wrap">
        <Group gap="xs">
          <ActionIcon variant="default" onClick={() => step(-1)} aria-label="Wcześniej">
            <IconChevronLeft size={16} />
          </ActionIcon>
          <Button variant="default" size="xs" onClick={() => setAnchor(today)}>
            Dziś
          </Button>
          <ActionIcon variant="default" onClick={() => step(1)} aria-label="Dalej">
            <IconChevronRight size={16} />
          </ActionIcon>
          <Text size="sm" fw={500}>
            {mode === 'dzien' ? longDay(anchor) : mode === 'miesiac' ? monthPl(monthStart) : dayRange(from, to)}
          </Text>
          {q.isFetching && <Loader size="xs" />}
        </Group>
        <Group gap="xs" wrap="wrap">
          <SegmentedControl
            size="xs"
            value={mode}
            onChange={(v) => setMode(v as Mode)}
            data={[
              { value: 'dzien', label: 'Dzień' },
              { value: 'tydzien', label: 'Tydzień' },
              { value: 'miesiac', label: 'Miesiąc' },
            ]}
          />
          <Select size="xs" placeholder="Wszystkie ekipy" clearable data={(teams.data ?? []).map((t) => ({ value: t.id, label: t.name }))} value={teamId} onChange={setTeamId} w={{ base: '100%', sm: 180 }} />
          <MultiSelect
            size="xs"
            placeholder={mode === 'miesiac' ? 'Pracownik (domyślnie Ty)' : 'Pracownicy'}
            searchable
            data={(q.data?.people ?? []).map((p) => ({ value: p.id, label: p.fullName }))}
            value={userIds}
            onChange={setUserIds}
            maxValues={mode === 'miesiac' ? 1 : undefined}
            w={{ base: '100%', sm: 220 }}
          />
        </Group>
      </Group>

      <Card withBorder padding={0}>
        <QueryState isLoading={q.isLoading} error={q.error}>
          {mode === 'tydzien' && (
            <ScrollArea type="auto">
              <Box style={{ display: 'grid', gridTemplateColumns: WEEK_COLS, minWidth: 1000 }}>
                <Text size="sm" c="dimmed" fw={500} px={10} style={{ alignSelf: 'center' }}>
                  Pracownik
                </Text>
                {days.map((d) => (
                  <DayHead key={d} d={d} today={today} />
                ))}
              </Box>
              {/* Flota (docs/19 §6): niedostępne pojazdy i kolizje z pracami — informacyjnie */}
              <FleetCalendarRows me={me} from={from} to={to} days={days} today={today} cols={WEEK_COLS} />
              {q.data?.people.map((p) => {
                // kolejne dni tej samej pracy — jeden słupek (wpisy z godzinami zostają kafelkami dnia)
                const bars = calendarBars(days, itemsOfUser(p.id));
                const laneCount = Math.max(1, ...bars.map((b) => b.lane + 1));
                return (
                  <Box
                    key={p.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: WEEK_COLS,
                      gridTemplateRows: `4px repeat(${laneCount}, auto) minmax(4px, 1fr)`,
                      rowGap: 3,
                      minWidth: 1000,
                      minHeight: 58,
                      borderTop: '1px solid var(--app-line)',
                    }}
                  >
                    <Text size="sm" fw={500} px={10} py={8} truncate style={{ gridColumn: 1, gridRow: '1 / -1' }}>
                      {p.fullName}
                    </Text>
                    {days.map((d, n) => (
                      <Box
                        key={d}
                        onDoubleClick={() => setNewEntry({ kind: 'nieobecnosc', day: d })}
                        style={{
                          gridColumn: n + 2,
                          gridRow: '1 / -1',
                          borderLeft: '1px solid var(--app-line)',
                          background: d === today ? 'var(--app-today)' : dayLabel(d).weekend ? 'var(--app-subtle)' : undefined,
                        }}
                      />
                    ))}
                    {bars.map((b) => (
                      <Box key={`${b.item.refId}:${b.item.day}:${b.col}`} style={{ gridColumn: `${b.col + 2} / span ${b.span}`, gridRow: b.lane + 2, minWidth: 0, zIndex: 1 }}>
                        <Item i={b.item} onOpen={open} edges={{ start: b.startsHere, end: b.endsHere }} />
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </ScrollArea>
          )}

          {mode === 'miesiac' && (
            <Box>
              <Text size="md" fw={600} p="sm">
                {people.get(monthPerson) ?? 'Wybierz pracownika'}
              </Text>
              <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
                {WEEKDAYS.map((w, n) => (
                  <Text key={w} size="sm" fw={600} tt="uppercase" c={n >= 5 ? 'red' : 'dimmed'} ta="center" py={6} style={{ borderBottom: '1px solid var(--app-line-strong)' }}>
                    {w}
                  </Text>
                ))}
              </Box>
              {Array.from({ length: Math.ceil(days.length / 7) }, (_, w) => days.slice(w * 7, w * 7 + 7)).map((week) => {
                // tydzień miesiąca: kolejne dni tej samej pracy — jeden słupek przez dni
                const bars = calendarBars(week, itemsOfUser(monthPerson));
                const laneCount = Math.max(1, ...bars.map((b) => b.lane + 1));
                return (
                  <Box
                    key={week[0]}
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gridTemplateRows: `30px repeat(${laneCount}, auto) minmax(6px, 1fr)`, rowGap: 3, minHeight: 130 }}
                  >
                    {week.map((d, n) => (
                      <Box
                        key={d}
                        p={6}
                        style={{
                          gridColumn: n + 1,
                          gridRow: '1 / -1',
                          border: '1px solid var(--app-line)',
                          opacity: d.slice(0, 7) === monthStart.slice(0, 7) ? 1 : 0.4,
                          background: d === today ? 'var(--app-today)' : dayLabel(d).weekend ? 'var(--app-subtle)' : undefined,
                        }}
                      >
                        <Text size="sm" fw={700} c={d === today ? 'blue.7' : dayLabel(d).weekend ? 'red' : undefined}>
                          {Number(d.slice(8))}
                          {d === today ? ' · dziś' : ''}
                        </Text>
                      </Box>
                    ))}
                    {bars.map((b) => (
                      <Box
                        key={`${b.item.refId}:${b.item.day}:${b.col}`}
                        style={{ gridColumn: `${b.col + 1} / span ${b.span}`, gridRow: b.lane + 2, minWidth: 0, zIndex: 1, opacity: week[b.col]!.slice(0, 7) === monthStart.slice(0, 7) ? 1 : 0.5 }}
                      >
                        <Item i={b.item} onOpen={open} edges={{ start: b.startsHere, end: b.endsHere }} />
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>
          )}

          {mode === 'dzien' && (
            <ScrollArea type="auto">
              <Box style={{ display: 'grid', gridTemplateColumns: `64px repeat(${Math.max(1, q.data?.people.length ?? 1)}, minmax(160px, 1fr))`, minWidth: 600 }}>
                <Box />
                {q.data?.people.map((p) => (
                  <Box key={p.id} p={6} style={{ borderLeft: '1px solid var(--app-line)' }}>
                    <Text size="sm" fw={600} truncate mb={4}>
                      {p.fullName}
                    </Text>
                    {/* cały dzień — pasek u góry kolumny */}
                    {itemsOf(p.id, anchor)
                      .filter((i) => !i.timeFrom)
                      .map((i, n) => (
                        <Item key={n} i={i} onOpen={open} />
                      ))}
                  </Box>
                ))}
                <Box>
                  {HOURS.map((h) => (
                    <Text key={h} size="sm" c="dimmed" h={HOUR_H} ta="right" pr={6} style={{ borderTop: '1px solid var(--app-line)' }}>
                      {timeHm(h)}
                    </Text>
                  ))}
                </Box>
                {q.data?.people.map((p) => (
                  <Box key={p.id} pos="relative" style={{ borderLeft: '1px solid var(--app-line)' }}>
                    {HOURS.map((h) => (
                      <Box key={h} h={HOUR_H} style={{ borderTop: '1px solid var(--app-line)' }} />
                    ))}
                    {itemsOf(p.id, anchor)
                      .filter((i) => i.timeFrom)
                      .map((i, n) => {
                        const top = ((Math.max(toMin(i.timeFrom!), 360) - 360) / 60) * HOUR_H;
                        const h = ((Math.min(toMin(i.timeTo!), 1140) - Math.max(toMin(i.timeFrom!), 360)) / 60) * HOUR_H;
                        return (
                          <Box key={n} pos="absolute" left={3} right={3} top={top + 1} h={Math.max(h - 2, 26)} style={{ overflow: 'hidden' }}>
                            <Item i={i} onOpen={open} fill />
                          </Box>
                        );
                      })}
                  </Box>
                ))}
              </Box>
            </ScrollArea>
          )}
          {!q.data?.people.length && <EmptyState compact title="Brak pracowników w wybranym filtrze" hint="Zmień ekipę albo wybór pracowników." />}
        </QueryState>
      </Card>
      <Text size="xs" c="dimmed" mt={4}>
        Dwuklik w komórce widoku tygodnia — nieobecność w tym dniu. Przerywana ramka — wniosek czekający na decyzję.
      </Text>
      {newEntry && <EntryDialog me={me} kind={newEntry.kind} day={newEntry.day} initial={newEntry.initial} onClose={() => setNewEntry(null)} />}
      {task && <TaskPanel me={me} taskId={task} onClose={() => setTask(null)} />}
      {entry && <EntryPanel me={me} entry={entry} people={people} onClose={() => setEntry(null)} />}
    </>
  );
}
